import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    basePath: String,
    defaultTab: String,
    activeTab: String,
    useQueryParams: Boolean,
    updateUrl: { type: Boolean, default: false },
  };

  connect() {
    this.Tab = window.bootstrap && window.bootstrap.Tab;
    this.restoreFromHistory = (event) => this.#restoreFromHistory(event);
    window.addEventListener('popstate', this.restoreFromHistory);

    const tab = this.#getTabFromUrl();
    if (tab) this.#switchToTab(tab, { replace: true });
  }

  disconnect() {
    window.removeEventListener('popstate', this.restoreFromHistory);
  }

  // Untrusted events are programmatic clicks fired during page setup (e.g. undistractor-tab-filter
  // activating the first tab on connect) — those must replaceState, not pushState, or every page
  // load adds a history entry and the back button gets trapped on the page
  handleTabClick(event) {
    const tab = event.params.tab;
    this.#switchToTab(tab, { replace: !event.isTrusted });
  }

  switchTo(event) {
    event.preventDefault();
    const tab = event.params.tab;
    if (!tab) return;

    this.#switchToTab(tab, { replace: !event.isTrusted });
  }

  // Tab entries carry empty state (see #updateUrl), so Turbo skips them on popstate and nothing else
  // moves the DOM back in step with the URL. Entries holding Turbo state are Turbo's to restore.
  //
  // Replaying the click is what keeps this honest: the other controllers listening on the same button
  // are what re-filter the rows and re-sync the sidebar, so activating the tab directly would leave
  // stale content under a switched header. Untrusted, so the replaceState below is the URL we are
  // already on.
  #restoreFromHistory(event) {
    if (event.state?.turbo) return;

    const tab = this.#parseTabFromUrl();
    if (!tab) return;

    this.element.querySelector(`[data-persistent-tabs-tab-param="${tab}"]`)?.click();
  }

  #getTabFromUrl() {
    return this.activeTabValue || this.#parseTabFromUrl() || this.defaultTabValue;
  }

  // Reads the tab from the URL alone, ignoring activeTabValue — that is fixed at render time, so it
  // goes stale as soon as a tab switch pushStates over it. Mirrors the two shapes #updateUrl writes:
  // lite has only a root route, so its tab lives in ?tab= rather than a path segment
  #parseTabFromUrl() {
    if (!this.updateUrlValue) return null;

    if (this.useQueryParamsValue) return this.#tabSlug(new URLSearchParams(window.location.search).get('tab'));

    const expectedPrefix = `${this.basePathValue}/`;
    const pathname = window.location.pathname;
    if (!pathname.startsWith(expectedPrefix)) return null;

    return this.#tabSlug(pathname.slice(expectedPrefix.length));
  }

  // ?tab= is unvalidated user input, and the slug reaches #activateBootstrapTab's attribute selector
  #tabSlug(tab) {
    return tab && /^[a-z0-9_-]+$/.test(tab) ? tab : null;
  }

  #switchToTab(tab, { replace = false } = {}) {
    this.#toggleAllTabsMode(tab);
    this.#activateBootstrapTab(tab);
    if (this.updateUrlValue) this.#updateUrl(tab, { replace });
  }

  // Query mode merges ?tab= into the existing query: the first tab replaceStates on connect, so replacing
  // would strip inbound utm_* off every landing. Path mode keeps search + hash so filters and anchors survive.
  #updateUrl(tab, { replace = false } = {}) {
    let newUrl;
    if (this.useQueryParamsValue) {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', tab);
      newUrl = `/?${params}`;
    } else {
      newUrl = `${this.basePathValue}/${tab}${window.location.search}${window.location.hash}`;
    }
    // Deliberately empty state: copying Turbo's state onto tab entries makes back/forward
    // trigger Turbo restore-visit fetches, whose body swap loses the extension's DOM reveal
    // (extension content scripts only run on full page loads)
    window.history[replace ? 'replaceState' : 'pushState']({}, '', newUrl);
  }

  #activateBootstrapTab(tab) {
    const btn = this.element.querySelector(`[data-persistent-tabs-tab-param="${tab}"]`);
    if (btn && this.Tab) {
      this.Tab.getOrCreateInstance(btn).show();
    }
  }

  #toggleAllTabsMode(tab) {
    const tabContent = this.element.querySelector('.tab-content');
    if (!tabContent) return;

    tabContent.classList.toggle('all-tabs-active', tab === 'all');
  }
}
